import { useContext, createContext, useMemo, useState, useEffect } from 'react';
import { firebaseGetToken, signInWCustomToken } from '~/services/firebase';
import { jwtDecode } from 'jwt-decode';
import { useCheckSessionSSO, useLogout } from '~/services/api/auth';
import { BroadcastEvents, channel } from '~/lib/broadcast';
import { useLocation } from 'react-router-dom';

export interface UnifiedAuthProps {
  isVerifyingSSO: boolean;
  setVerifyingSSO: (v: boolean) => void;
  isLoginLoading: boolean;
}
export const UnifiedAuthContext = createContext({} as UnifiedAuthProps);

const UnifiedAuthProvider = ({ children }) => {
  const location = useLocation();

  const [isVerifyingSSO, setVerifyingSSO] = useState(true);
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  const { mutate: logoutMutation } = useLogout({
    onSuccess: () => {
      window.location.reload();
    },
    onError: () => {
      setVerifyingSSO(false);
    },
  });

  const { data: tokenInfoSSO, isFetching: isFetchingCheckSession } =
    useCheckSessionSSO({
      enabled: !isLoginLoading,
      onError: async () => {
        const idToken = await firebaseGetToken();
        if (idToken) {
          logoutMutation();
        }
      },
    });

  const isFirebaseUidEqual = (customTk: string, idTk: string): boolean => {
    if (!customTk || !idTk) return false;
    const customTkDecoded = jwtDecode<{ uid: string }>(customTk);
    const idTkDecoded = jwtDecode<{ user_id: string }>(idTk);
    return customTkDecoded?.uid === idTkDecoded?.user_id;
  };

  const newVerifyToken = async (customToken: string) => {
    const idToken = await firebaseGetToken();
    if (customToken) {
      try {
        const isLogged = !!idToken;
        const isSameAccount = isFirebaseUidEqual(customToken, idToken);
        if (!isLogged || (isLogged && !isSameAccount)) {
          await signInWCustomToken(customToken);
          channel.postMessage({
            type: BroadcastEvents.RefreshOtherPageWhenLogged,
          });
          if (
            location.pathname.includes('/invite-user') &&
            isLogged &&
            !isSameAccount
          ) {
            window.location.href = '/';
          } else window.location.reload();
        }
      } catch (err) {
        logoutMutation();
      } finally {
        setVerifyingSSO(false);
      }
    } else if (idToken) {
      logoutMutation();
    } else {
      setVerifyingSSO(false);
    }
  };

  // sync data multi tab
  useEffect(() => {
    const syncData = (d) => {
      const { type, payload } = d;
      if (type === BroadcastEvents.SyncLoginState) {
        setIsLoginLoading(payload);
      }
      if (type === BroadcastEvents.RefreshOtherPageWhenLogout) {
        window.location.reload();
      }
    };
    channel.addEventListener('message', syncData);
    return () => {
      channel.removeEventListener('message', syncData);
    };
  }, []);

  useEffect(() => {
    if (!isFetchingCheckSession) {
      const customToken = tokenInfoSSO?.customToken;
      newVerifyToken(customToken);
    }
  }, [tokenInfoSSO, isFetchingCheckSession]);

  const values = useMemo(
    () => ({
      isVerifyingSSO,
      setVerifyingSSO,
      isLoginLoading,
    }),
    [isVerifyingSSO, setVerifyingSSO, isLoginLoading],
  );

  return (
    <UnifiedAuthContext.Provider value={values}>
      {children}
    </UnifiedAuthContext.Provider>
  );
};

const useUnifiedAuthContext = () => {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error(
      'Components cannot be rendered outside the Unified Auth Context',
    );
  }
  return context;
};

export { UnifiedAuthProvider, useUnifiedAuthContext };
